import * as React from "react";
import { useEffect, useRef } from "react";
import {
  useRive,
  useStateMachineInput,
  Layout,
  Fit,
  Alignment,
  type StateMachineInput,
  type Rive,
} from "@rive-app/react-canvas";

export interface LaunchablesRiveInputs {
  rive: Rive;
  getInput: (name: string) => StateMachineInput | null;
  listInputs: () => string[];
}

interface LaunchablesRiveProps {
  className?: string;
  stateMachine?: string;
  artboard?: string;
  fit?: Fit;
  alignment?: Alignment;
  scale?: number;
  onLoad?: (inputs: LaunchablesRiveInputs) => void;
}

export function LaunchablesRive({
  className,
  stateMachine = "State Machine 1",
  artboard,
  fit = Fit.Contain,
  alignment = Alignment.Center,
  scale = 1,
  onLoad,
}: LaunchablesRiveProps) {
  const hasCalledOnLoad = useRef(false);

  const { rive, RiveComponent } = useRive({
    src: "/rive/launchables.riv",
    stateMachines: stateMachine,
    artboard,
    autoplay: true,
    layout: new Layout({ fit, alignment }),
  });

  useEffect(() => {
    if (rive && onLoad && !hasCalledOnLoad.current) {
      hasCalledOnLoad.current = true;
      onLoad({
        rive,
        getInput: (name: string) => {
          const inputs = rive.stateMachineInputs(stateMachine);
          return inputs?.find((input) => input.name === name) ?? null;
        },
        listInputs: () => {
          const inputs = rive.stateMachineInputs(stateMachine);
          return inputs?.map((input) => input.name) ?? [];
        },
      });
    }
  }, [rive, onLoad, stateMachine]);

  return (
    <div
      className={className}
      style={scale !== 1 ? { transform: `scale(${scale})` } : undefined}
    >
      <RiveComponent />
    </div>
  );
}

export { useStateMachineInput };
